import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { consumeInstallPrompt, getInstallPrompt, INSTALL_CHANGED, isAppInstalled } from './installPrompt';

export default function InstallAppDialog({ open, onClose }) {
    const [prompt, setPrompt] = useState(getInstallPrompt);
    const [installed, setInstalled] = useState(isAppInstalled);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const android = /Android/i.test(ua);
    const edge = /Edg\//.test(ua);
    const safari = /Safari/.test(ua) && !/Chrome|Chromium|Edg/.test(ua);
    const appsAddress = edge ? 'edge://apps' : 'chrome://apps';
    const calendarUrl = new URL('/biz/calendar?v=booking', window.location.origin).href;
    const localPreview = ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

    useEffect(() => {
        if (open) setMessage('');
    }, [open]);

    useEffect(() => {
        const display = window.matchMedia('(display-mode: standalone)');
        const update = () => {
            setPrompt(getInstallPrompt());
            setInstalled(isAppInstalled());
        };
        update();
        window.addEventListener(INSTALL_CHANGED, update);
        display.addEventListener('change', update);
        return () => {
            window.removeEventListener(INSTALL_CHANGED, update);
            display.removeEventListener('change', update);
        };
    }, []);

    const install = async () => {
        const availablePrompt = consumeInstallPrompt();
        if (!availablePrompt) return;
        setBusy(true);
        setMessage('');
        try {
            await availablePrompt.prompt();
            const choice = await availablePrompt.userChoice;
            setMessage(choice.outcome === 'accepted'
                ? 'Follow the browser confirmation to finish installing Timely365.'
                : 'Installation was canceled. You can still add a shortcut using the steps below.');
        } catch {
            setMessage('The browser could not open installation. Use the steps below.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="timely-install-title">
            <DialogTitle id="timely-install-title">Add Timely365 to your device</DialogTitle>
            <DialogContent dividers>
                <Typography sx={{ mb: 2 }}>Add an icon so you can open your calendar quickly.</Typography>
                {installed && <Alert severity="success" sx={{ mb: 2 }}>Timely365 is installed or open as an app on this device.</Alert>}
                {prompt && !installed && <Box sx={{ mb: 3 }}>
                    <Button variant="contained" size="large" disabled={busy} onClick={install}>Install Timely365</Button>
                    <Typography variant="body2" sx={{ mt: 1 }}>Click Install Timely365, then choose Install in the browser window that appears.</Typography>
                </Box>}
                {message && <Alert severity="info" role="status" sx={{ mb: 2 }}>{message}</Alert>}
                <>
                    <Typography variant="h6">{ios || android ? 'Add to your home screen' : 'Install on your computer'}</Typography>
                    <Box component="ol" sx={{ pl: 3, '& li': { mb: 1 } }}>
                        {ios ? <>
                            <li>Open Timely365 in <strong>Safari</strong>.</li>
                            <li>Tap Share, then Add to Home Screen.</li>
                            <li>Keep <strong>Open as Web App</strong> enabled if shown, then tap <strong>Add</strong>.</li>
                        </> : android ? <>
                            <li>Open Timely365 in <strong>Chrome</strong>.</li>
                            <li>Tap the <strong>three dots</strong> at the top right.</li>
                            <li>Tap <strong>Add to Home screen</strong> or <strong>Install app</strong>, then confirm.</li>
                        </> : safari ? <>
                            <li>In Safari, click <strong>File</strong> at the top of your screen.</li>
                            <li>Click <strong>Add to Dock</strong>, then <strong>Add</strong>.</li>
                        </> : <>
                            <li>Open Timely365 in Chrome or Microsoft Edge.</li>
                            <li>Click the <strong>three dots</strong> at the top right.</li>
                            <li>{edge ? 'Choose Apps, then Install this site as an app.' : 'Choose Cast, save, and share, then Install page as app.'}</li>
                            <li>Click <strong>Install</strong>.</li>
                        </>}
                    </Box>
                </>
                {!ios && !android && !prompt && !installed && <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">If you are viewing this inside another app, copy this address and open it in Chrome or Edge first.</Typography>
                    <Typography sx={{ my: 1, overflowWrap: 'anywhere' }}>{calendarUrl}</Typography>
                    <Button variant="outlined" onClick={async () => {
                        try {
                            await navigator.clipboard.writeText(calendarUrl);
                            setMessage('Address copied. Open Chrome or Edge, paste it into the address bar, and press Enter. Then follow the install steps above.');
                        } catch {
                            setMessage('Select and copy the address shown above, then paste it into Chrome or Edge.');
                        }
                    }}>Copy Timely365 address</Button>
                </Box>}
                {localPreview && <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>You are using the local preview. Its shortcut works on this computer while the local server is running.</Typography>}
                {!ios && !android && !safari && <Box component="details" sx={{ mt: 3 }}>
                    <Box component="summary" sx={{ cursor: 'pointer', fontWeight: 600 }}>Already installed, but lost your app icon?</Box>
                    <Typography sx={{ mt: 1 }}>In the browser you installed it with, open {appsAddress}. Find Timely365 and choose {edge ? 'Details, then Create desktop shortcut.' : 'Create shortcut.'}</Typography>
                </Box>}
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Done</Button></DialogActions>
        </Dialog>
    );
}
